import * as React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import CheckoutHeader from "./CheckoutHeader";
import ScrollToTopForService from "../../components/customer/ScrollToTopForService";
import CustomerFooter from "./CustomerFooter";
import { useLazyGetCheckoutSummaryQuery } from "../../redux/actions/Customer/cartCustomer";

const CheckoutLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [mode, setMode] = React.useState<"Products" | "Pets" | "Services">(
    () => {
      return (
        (localStorage.getItem("selectedMode") as
          | "Products"
          | "Pets"
          | "Services") || "Products"
      );
    },
  );

  const [triggerGetCheckoutSummary, { isFetching: isCheckingCheckout }] =
    useLazyGetCheckoutSummaryQuery();

  const [checkoutGuardReady, setCheckoutGuardReady] = React.useState(false);

  React.useEffect(() => {
    localStorage.setItem("selectedMode", mode);
  }, [mode]);

  const hideFooterRoutes = [
    "/cartpage/customeraddresspage",
    "/cartpage/customerpaymentpage",
  ];

  const shouldHideFooter = hideFooterRoutes.some((route) =>
    location.pathname.includes(route),
  );

  const isCheckoutProtectedPage =
    location.pathname === "/cartpage/customeraddresspage";

  React.useEffect(() => {
    if (!isCheckoutProtectedPage) {
      setCheckoutGuardReady(true);
      return;
    }

    let cancelled = false;

    const validateCheckout = async () => {
      try {
        setCheckoutGuardReady(false);

        const response = await triggerGetCheckoutSummary().unwrap();

        if (cancelled) return;

        const totalItems = response?.cart?.totalItems ?? 0;

        // -----------------------------------------
        // No cart items
        // -----------------------------------------

        if (totalItems <= 0) {
          navigate("/", {
            replace: true,
          });

          return;
        }

        navigate("/cartpage/customercartpage", {
          replace: true,
        });

        // -----------------------------------------
        // Cart has items
        //
        // If user directly opened address/payment
        // page, don't allow them to bypass cart.
        // -----------------------------------------

        if (location.pathname === "/cartpage/customeraddresspage") {
          navigate("/cartpage/customercartpage", {
            replace: true,
          });

          return;
        }

        setCheckoutGuardReady(true);
      } catch (error) {
        console.error("Checkout validation failed:", error);

        if (cancelled) return;

        // If checkout summary itself says cart/session
        // is unavailable, return to home.
        navigate("/", {
          replace: true,
        });
      } finally {
        if (!cancelled) {
          setCheckoutGuardReady(true);
        }
      }
    };

    validateCheckout();

    return () => {
      cancelled = true;
    };
  }, [
    isCheckoutProtectedPage,
    location.pathname,
    navigate,
    triggerGetCheckoutSummary,
  ]);

  const handleModeChange = (newMode: "Products" | "Pets" | "Services") => {
    setMode(newMode);

    if (location.pathname !== "/") {
      navigate("/");
    }
  };

  // ---------------------------------------------
  // Prevent checkout page from flashing while
  // validation is happening
  // ---------------------------------------------

  if (isCheckoutProtectedPage && !checkoutGuardReady) {
    return (
      <div className="bg-white w-full min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-logoHeading mx-auto" />
          <p className="mt-3 text-gray-600">Checking your checkout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white w-full min-h-screen">
      <div className="sticky top-0 z-50 bg-logoHeading">
        <div className="max-w-7xl mx-auto w-full bg-logoHeading">
          <CheckoutHeader />
        </div>
      </div>

      <ScrollToTopForService mode={mode} />

      <div>
        <Outlet context={{ mode }} />
      </div>

      {!shouldHideFooter && (
        <CustomerFooter
          showWave={false}
          className="pt-10"
          setCurrentMode={handleModeChange}
        />
      )}
    </div>
  );
};

export default CheckoutLayout;
